/**
 * DBN Risk & Additionality — PAR Metrics + Scenario Analysis + Risk Heatmap + Guarantee Simulator
 */
import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import { dbnApi, KPICard, SectionHeader, Badge, PageHeader, LoadingState,
  fmtEur, fmtN, fmtPct, ProgressBar } from "./dbnUtils";
import { Shield, TrendingUp, DollarSign, Zap, AlertTriangle } from "lucide-react";

// ── Gauge ─────────────────────────────────────────────────────────────────────
function GaugeCard({ label, value, max=100, color="#34d399", sub }) {
  const pct = Math.min(value/max*100, 100);
  const radius = 36, circ = 2*Math.PI*radius, dash = (pct/100)*circ;
  return (
    <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, textAlign:"center" }}>
      <svg width={90} height={90} style={{ margin:"0 auto 8px", display:"block" }}>
        <circle cx={45} cy={45} r={radius} fill="none" stroke="#0f2033" strokeWidth={7} />
        <circle cx={45} cy={45} r={radius} fill="none" stroke={color} strokeWidth={7}
          strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
          strokeLinecap="round" style={{ transition:"stroke-dasharray .5s" }} />
        <text x={45} y={48} textAnchor="middle" fill={color} fontSize={16} fontWeight={900}>{value}</text>
      </svg>
      <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{label}</p>
      {sub && <p style={{ fontSize:9, color:"#475569", marginTop:2 }}>{sub}</p>}
    </div>
  );
}

// ── PAR Metrics Panel ──────────────────────────────────────────────────────────
function PARPanel({ parData }) {
  const m = parData?.par_metrics || {};
  const sectors = parData?.sector_concentration || [];
  const states  = parData?.state_concentration || [];

  return (
    <div>
      {/* Key PAR metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          { l:"Portfolio at Risk (High)",  v:fmtEur(m.par_high),              c:"#ef4444", sub:"High-risk projects" },
          { l:"Expected Loss",             v:fmtEur(m.expected_loss_eur),     c:"#f97316", sub:`${m.expected_loss_pct}% of portfolio` },
          { l:"Probability of Default",    v:`${m.pd_portfolio_pct}%`,        c:"#f59e0b", sub:`LGD assumption: ${m.lgd_assumption_pct}%` },
          { l:"Diversification Index",     v:`${m.diversification_index}/100`, c:"#34d399", sub:"Higher = more diversified" },
        ].map(x => (
          <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
            borderTop:`3px solid ${x.c}`, borderRadius:12, padding:"12px 14px" }}>
            <p style={{ fontSize:18, fontWeight:900, color:x.c }}>{x.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em", lineHeight:1.4 }}>{x.l}</p>
            <p style={{ fontSize:8, color:"#334155", marginTop:2 }}>{x.sub}</p>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
        {/* Concentration metrics */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
          <SectionHeader title="Concentration Risk" sub="Max exposure by dimension" />
          {[
            ["Sector Concentration", m.max_sector_concentration_pct, m.max_sector_concentration_pct > 35 ? "#ef4444" : "#f59e0b"],
            ["State Concentration",  m.max_state_concentration_pct,  m.max_state_concentration_pct > 25  ? "#f59e0b" : "#34d399"],
            ["PFI Concentration",    m.max_pfi_concentration_pct,    m.max_pfi_concentration_pct > 20    ? "#f59e0b" : "#34d399"],
            ["Currency Exposure",    m.currency_exposure_pct,        "#60a5fa"],
            ["Climate Exposed",      m.climate_exposed_pct,          m.climate_exposed_pct > 40 ? "#f97316" : "#f59e0b"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                <span style={{ fontSize:9, color:"#64748b" }}>{l}</span>
                <span style={{ fontSize:10, fontWeight:700, color:c }}>{v}%</span>
              </div>
              <ProgressBar value={v} color={c} height={4} showPct={false} />
            </div>
          ))}
        </div>

        {/* Sector concentration */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
          <SectionHeader title="Sector Breakdown" sub="Finance exposure %" />
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {sectors.slice(0,7).map(s => (
              <div key={s.sector} style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.sector.split(" ")[0]}</span>
                <div style={{ width:80, height:5, background:"#0f2033", borderRadius:3 }}>
                  <div style={{ height:5, width:`${s.pct}%`, background:"#34d399", borderRadius:3 }} />
                </div>
                <span style={{ fontSize:9, color:"#64748b", width:28, textAlign:"right" }}>{s.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* State concentration */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
          <SectionHeader title="State Breakdown" sub="Finance exposure %" />
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {states.slice(0,7).map(s => (
              <div key={s.state} style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.state}</span>
                <div style={{ width:80, height:5, background:"#0f2033", borderRadius:3 }}>
                  <div style={{ height:5, width:`${s.pct}%`, background:"#60a5fa", borderRadius:3 }} />
                </div>
                <span style={{ fontSize:9, color:"#64748b", width:28, textAlign:"right" }}>{s.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Heatmap */}
      <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
        <SectionHeader title="Project Risk Heatmap" sub="Risk dimension scores — red = higher risk" />
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:500 }}>
            <thead>
              <tr>
                {["Project","Financial Risk","Climate Risk","ESG Risk","Additionality Risk","Overall"].map(h => (
                  <th key={h} style={{ background:"#060f1e", color:"#475569", fontSize:8, fontWeight:700,
                    padding:"6px 8px", textAlign:"center", textTransform:"uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(parData?.risk_heatmap || []).map(r => (
                <tr key={r.id}>
                  <td style={{ padding:"5px 8px", fontSize:9, color:"#94a3b8", borderBottom:"1px solid #0f1e33" }}>{r.name}</td>
                  {[r.financial_risk, r.climate_risk, r.esg_risk||40, r.additionality_risk, r.overall_risk].map((v, i) => {
                    const bg = v >= 70 ? "#7f1d1d" : v >= 50 ? "#451a03" : "#064e3b";
                    const tc = v >= 70 ? "#fca5a5" : v >= 50 ? "#fed7aa" : "#86efac";
                    return (
                      <td key={i} style={{ padding:"5px 8px", textAlign:"center", borderBottom:"1px solid #0f1e33",
                        background:bg }}>
                        <span style={{ fontSize:9, fontWeight:700, color:tc }}>{v}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Scenario Analysis ─────────────────────────────────────────────────────────
function ScenarioAnalysis({ data }) {
  const [scenarioType, setScenarioType] = useState("interest_rate");
  const s = data || {};

  const scenarios = {
    interest_rate: {
      label:"Interest Rate Stress", color:"#f97316",
      data: s.interest_rate || {},
      keys: ["base","up200","up400","down100"],
      cols: ["Scenario","Rate Shift","NPV Impact","Default Rate","Projects Affected"],
      extract: (k,d) => [d.label||k, d.rate_shift>0?`+${d.rate_shift}bps`:d.rate_shift<0?`${d.rate_shift}bps`:"Base",
        `${d.npv_impact_pct>0?"+":""}${d.npv_impact_pct||0}%`, `${d.default_rate_pct||0}%`, d.affected_projects||0],
    },
    fx: {
      label:"FX Stress", color:"#a78bfa",
      data: s.fx || {},
      keys: ["current","mild","severe","extreme"],
      cols: ["Scenario","NGN/EUR Rate","Portfolio NGN","Repayment Impact"],
      extract: (k,d) => [d.label||k, `₦${(d.ngn_eur||0).toLocaleString()}`,
        `₦${((d.portfolio_ngn||0)/1e9).toFixed(2)}B`, `${d.repayment_impact_pct>0?"+":""}${d.repayment_impact_pct||0}%`],
    },
    climate: {
      label:"Climate Risk", color:"#34d399",
      data: s.climate || {},
      keys: ["baseline","moderate","severe","extreme"],
      cols: ["Scenario","Portfolio at Risk","Projects Affected","Loss Estimate"],
      extract: (k,d) => [d.label||k, `${d.portfolio_at_risk_pct||0}%`, d.projects_affected||0, fmtEur(d.loss_estimate_eur||0)],
    },
  };

  const curr = scenarios[scenarioType];
  const rows = curr.keys.map(k => curr.extract(k, curr.data[k]||{}));

  return (
    <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div>
          <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>Scenario Analysis</p>
          <p style={{ fontSize:11, color:"#475569" }}>Stress testing across interest rate, FX and climate dimensions</p>
        </div>
        <div style={{ display:"flex", gap:4 }}>
          {Object.entries(scenarios).map(([key, sc]) => (
            <button key={key} onClick={() => setScenarioType(key)}
              style={{ padding:"5px 10px", borderRadius:7, fontSize:9, fontWeight:700, border:`1px solid ${sc.color}`,
                background:scenarioType===key?sc.color:"transparent", color:scenarioType===key?"#07111e":sc.color, cursor:"pointer" }}>
              {sc.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              {curr.cols.map(col => (
                <th key={col} style={{ background:"#060f1e", color:"#475569", fontSize:9, fontWeight:700,
                  padding:"7px 10px", textAlign:"left", textTransform:"uppercase", letterSpacing:"0.08em" }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding:"8px 10px", fontSize:10,
                    color:j===0?"#f0f9ff":j===1?"#94a3b8":"#f0f9ff",
                    fontWeight:j===0?600:j===1?400:500,
                    borderBottom:"1px solid #0f1e33",
                    background:i===0?"rgba(52,211,153,0.05)":"transparent"
                  }}>
                    {i===0&&j>1 ? <span style={{ color:"#34d399" }}>{cell}</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Guarantee Simulator ───────────────────────────────────────────────────────
function GuaranteeSimulator({ summary, scenarios }) {
  const [coverage, setCoverage] = useState(50);
  const base = summary?.total_finance || 40_000_000;
  const addLending = base * (coverage / 100) * 1.8;
  const exposure = base * (coverage / 100) * 0.4;
  const jobs = Math.round(addLending / 5000);
  const ghg = Math.round(addLending / 1000 * 12);
  const sc = scenarios?.guarantee;
  const lookup = coverage <= 10 ? sc?.coverage_10 : coverage <= 25 ? sc?.coverage_25 : coverage <= 50 ? sc?.coverage_50 : sc?.coverage_75;

  return (
    <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
      <SectionHeader title="Guarantee Coverage Simulator" sub="Model additional lending, development impact and DBN exposure at different guarantee levels" />
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ fontSize:11, color:"#94a3b8" }}>
            Guarantee Coverage Level: <strong style={{ color:"#fbbf24" }}>{coverage}%</strong>
          </span>
          <span style={{ fontSize:10, color:"#475569" }}>Base portfolio: {fmtEur(base)}</span>
        </div>
        <input type="range" min={10} max={75} step={5} value={coverage}
          onChange={e=>setCoverage(+e.target.value)}
          style={{ width:"100%", accentColor:"#fbbf24", cursor:"pointer" }} />
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#334155" }}>
          <span>10%</span><span>25%</span><span>50%</span><span>75%</span>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10 }}>
        {[
          { l:"Additional Lending Mobilised",  v:fmtEur(lookup?.additional_lending||addLending),  c:"#34d399" },
          { l:"DBN Guarantee Exposure",         v:fmtEur(lookup?.exposure||exposure),               c:"#fbbf24" },
          { l:"Estimated Jobs Created",         v:fmtN(lookup?.jobs||jobs),                         c:"#60a5fa" },
          { l:"Estimated GHG Reduction",        v:`${fmtN(lookup?.ghg_tco2e||ghg)} tCO₂e`,         c:"#a78bfa" },
        ].map(m => (
          <div key={m.l} style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px", borderLeft:`3px solid ${m.c}` }}>
            <p style={{ fontSize:14, fontWeight:900, color:m.c }}>{m.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em", lineHeight:1.4 }}>{m.l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DBNRiskAdditionality() {
  const [data, setData] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [parData, setParData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("par");

  useEffect(() => {
    Promise.all([dbnApi.riskAdditionality(), dbnApi.scenarios(), dbnApi.riskPar()])
      .then(([r1, r2, r3]) => { setData(r1.data); setScenarios(r2.data); setParData(r3.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  const s = data?.summary || {};
  const ft = data?.funding_transmission || {};
  const add = data?.additionality_breakdown || {};

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Risk, Additionality and Capital Mobilisation"
        subtitle="Portfolio at risk · Scenario stress testing · Additionality evidence · Guarantee simulation" />

      <div style={{ padding:"20px 24px", flex:1 }}>
        {/* KPIs */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
          <KPICard label="Total Finance" value={fmtEur(s.total_finance)} sub="Active portfolio" color="#34d399" icon={DollarSign} />
          <KPICard label="Mobilisation Ratio" value={`${s.mobilisation_ratio}x`} sub="Total capital per EUR 1 DBN" color="#fbbf24" icon={Zap} />
          <KPICard label="Avg Additionality" value={`${s.avg_additionality_score}/100`} sub="Financial + project + development" color="#a78bfa" icon={Shield} />
          <KPICard label="Expected Loss" value={fmtEur(parData?.par_metrics?.expected_loss_eur)} sub={`${parData?.par_metrics?.expected_loss_pct || 0}% of portfolio`} color="#ef4444" icon={AlertTriangle} />
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:16, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
          {[
            {key:"par",           label:"Portfolio at Risk"},
            {key:"additionality", label:"Additionality"},
            {key:"scenarios",     label:"Scenario Analysis"},
            {key:"guarantee",     label:"Guarantee Simulator"},
            {key:"transmission",  label:"Funding Transmission"},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background:tab===t.key?"rgba(52,211,153,0.15)":"transparent",
                color:tab===t.key?"#34d399":"#475569" }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "par"           && <PARPanel parData={parData} />}
        {tab === "scenarios"     && <ScenarioAnalysis data={scenarios} />}
        {tab === "guarantee"     && <GuaranteeSimulator summary={s} scenarios={scenarios} />}

        {tab === "additionality" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
              <GaugeCard label="Financial Additionality" value={add.financial?.score||82} color="#34d399"
                sub="Longer tenor, lower rate, first formal financing" />
              <GaugeCard label="Project Additionality"   value={add.project?.score||79}   color="#60a5fa"
                sub="Would not proceed without DBN" />
              <GaugeCard label="Development Additionality" value={add.development?.score||84} color="#a78bfa"
                sub="Jobs, women enterprises, rural inclusion" />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
                <SectionHeader title="Mobilisation by Sector" sub="Total capital ÷ DBN finance" />
                {(data?.sector_mobilisation || []).map(m => (
                  <div key={m.sector} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <span style={{ fontSize:10, color:"#64748b", width:180 }}>{m.sector}</span>
                    <div style={{ flex:1, height:7, background:"#0f2033", borderRadius:4 }}>
                      <div style={{ height:7, width:`${Math.min((m.ratio/4)*100,100)}%`,
                        background:"linear-gradient(90deg,#34d399,#0d9488)", borderRadius:4 }} />
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:"#34d399", width:30, textAlign:"right" }}>{m.ratio}x</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
                <SectionHeader title="Portfolio Risk Distribution" sub="24 projects" />
                {Object.entries(data?.risk_distribution || {}).map(([risk, count]) => {
                  const colors = { Low:"#22c55e", Moderate:"#f59e0b", High:"#ef4444" };
                  const pct = Math.round(count/24*100);
                  return (
                    <div key={risk} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background:colors[risk] }} />
                          <span style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>{risk}</span>
                        </div>
                        <span style={{ fontSize:11, color:colors[risk], fontWeight:700 }}>{count} projects ({pct}%)</span>
                      </div>
                      <div style={{ height:8, background:"#0f2033", borderRadius:4 }}>
                        <div style={{ height:8, width:`${pct}%`, background:colors[risk], borderRadius:4 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {tab === "transmission" && (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
            <SectionHeader title="Development Finance Transmission" sub="Financing cost and terms from development partner to end MSME borrower" />
            <div style={{ display:"flex", alignItems:"stretch", gap:0, overflowX:"auto" }}>
              {(ft.stages || []).map((stage, i) => (
                <React.Fragment key={i}>
                  <div style={{ flex:1, background:"#060f1e", borderRadius:10, padding:"12px 14px", minWidth:140 }}>
                    <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>{stage.stage}</p>
                    <p style={{ fontSize:20, fontWeight:900, color:["#34d399","#60a5fa","#fbbf24","#f97316"][i]||"#94a3b8" }}>
                      {stage.rate?.toFixed(1)}%
                    </p>
                    <p style={{ fontSize:9, color:"#334155" }}>Cost of funds</p>
                    <p style={{ fontSize:11, color:"#64748b", marginTop:4 }}>{stage.tenor_yrs}yr · {stage.grace_yrs}yr grace</p>
                  </div>
                  {i < (ft.stages||[]).length-1 && (
                    <div style={{ display:"flex", alignItems:"center", padding:"0 6px" }}>
                      <div style={{ fontSize:16, color:"#1e3a5f" }}>→</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div style={{ marginTop:10, background:"#001a0a", border:"1px solid #004a1e", borderRadius:8, padding:"8px 12px",
              display:"flex", alignItems:"center", gap:8 }}>
              <Zap size={12} color="#34d399" />
              <p style={{ fontSize:10, color:"#065f46" }}>
                Development finance advantage: <strong style={{ color:"#34d399" }}>{ft.advantage_bp} bps</strong> below commercial rates.
                DBN delivers concessional capital at 9.8–14.5% vs commercial market rate of 26.5%.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
